import { Application } from '@platform/client';
import { ProfessionalApplicationStatusEnum } from '@platform/client/schemas';
import { Route, Switch, useLocation } from 'wouter';
import { ApplicationStorageDataKeys, Step } from '../Application';
import { ExperienceHopeSelection } from '../final-details/ExperienceHopeSelection';
import { ExperienceDetailSelection } from '../profile/shared/ExperienceDetailSelection';
import { LocationSelection } from '../profile/shared/LocationSelection';
import { ProfilePictureUpload } from '../profile/shared/ProfilePictureUpload';
import { RepeatVolunteerSelection } from '../profile/shared/RepeatVolunteerSelection';
import { ResumeSelection } from '../profile/shared/ResumeSelection';
import { ReckittWorkExperience } from '../program/reckitt/ReckittWorkExperience';
import { ImpactAreaSelection } from '../program/shared/ImpactAreaSelection';
import { ApplicationApproved } from '../result/shared/ApplicationApproved';
import { ApplicationRejected } from '../result/shared/ApplicationRejected';
import { ApplicationUnderReview } from '../result/shared/ApplicationUnderReview';
import { ApplicationWaitlisted } from '../result/shared/ApplicationWaitlisted';
import { SkillsImpactSelection } from '../skills/SkillsImpactSelection';
import { SkillsExperience } from '../skills/SkillsSelection';
import { ReckittReferralSelection } from '../program/reckitt/ReckittReferralSelection';
import { ReckittConfirmAndSubmit } from '../final-details/reckitt/ReckittConfirmAndSubmit';

enum ReckittApplicationSteps {
  RECKITT_EXPERIENCE_SELECTION = 'RECKITT_EXPERIENCE_SELECTION',
  IMPACT_AREA_SELECTION = 'IMPACT_AREA_SELECTION',
  PREVIOUS_VOLUNTEER_SELECTION = 'PREVIOUS_VOLUNTEER_SELECTION',

  LOCATION_SELECTION = 'LOCATION_SELECTION',
  RESUME_SELECTION = 'RESUME_SELECTION',
  EXPERIENCE_SELECTION = 'EXPERIENCE_SELECTION',
  PHOTO_SELECTION = 'PHOTO_SELECTION',

  SKILLS_SELECTION = 'SKILLS_SELECTION',
  SKILL_IMPACT_SELECTION = 'SKILL_IMPACT_SELECTION',

  EXPERIENCE_HOPE_SELECTION = 'EXPERIENCE_HOPE_SELECTION',
  REFERRAL_SELECTION = 'REFERRAL_SELECTION',
  CONFIRM_AND_SUBMIT = 'CONFIRM_AND_SUBMIT',
}

const ReckittApplicationStep: Record<ReckittApplicationSteps, Step> = {
  [ReckittApplicationSteps.RECKITT_EXPERIENCE_SELECTION]: {
    progress: 50,
    component: ReckittWorkExperience,
    url: '/',
  },
  [ReckittApplicationSteps.IMPACT_AREA_SELECTION]: {
    progress: 80,
    component: ImpactAreaSelection,
    url: '/impact-area-selection',
  },
  [ReckittApplicationSteps.PREVIOUS_VOLUNTEER_SELECTION]: {
    progress: 80,
    component: RepeatVolunteerSelection,
    url: '/previous-volunteer-selection',
  },
  [ReckittApplicationSteps.LOCATION_SELECTION]: {
    progress: 130,
    component: LocationSelection,
    url: '/location-selection',
  },
  [ReckittApplicationSteps.RESUME_SELECTION]: {
    progress: 150,
    component: ResumeSelection,
    url: '/resume-selection',
  },
  [ReckittApplicationSteps.EXPERIENCE_SELECTION]: {
    progress: 160,
    component: ExperienceDetailSelection,
    url: '/experience-selection',
  },
  [ReckittApplicationSteps.PHOTO_SELECTION]: {
    progress: 190,
    component: ProfilePictureUpload,
    url: '/photo-selection',
  },
  [ReckittApplicationSteps.SKILLS_SELECTION]: {
    progress: 220,
    component: SkillsExperience,
    url: '/skills-selection',
  },
  [ReckittApplicationSteps.SKILL_IMPACT_SELECTION]: {
    progress: 280,
    component: SkillsImpactSelection,
    url: '/skill-impact-selection',
  },
  [ReckittApplicationSteps.EXPERIENCE_HOPE_SELECTION]: {
    progress: 320,
    component: ExperienceHopeSelection,
    url: '/experience-hope-selection',
  },
  [ReckittApplicationSteps.REFERRAL_SELECTION]: {
    progress: 380,
    component: ReckittReferralSelection,
    url: '/referral-selection',
  },
  [ReckittApplicationSteps.CONFIRM_AND_SUBMIT]: {
    progress: 400,
    component: ReckittConfirmAndSubmit,
    url: '/confirm-and-submit',
  },
} as const;

export const ReckittApplicationRoot = () => {
  const [location, navigate] = useLocation();

  const goToNextStep = () => {
    const steps = Object.values(ReckittApplicationSteps);
    const currentIndex = steps.findIndex((step) => {
      return ReckittApplicationStep[step as ReckittApplicationSteps].url === location;
    });
    const nextStep = currentIndex < steps.length - 1 ? steps[currentIndex + 1] : location;
    const nextUrl = ReckittApplicationStep[nextStep as ReckittApplicationSteps].url;
    navigate(nextUrl);
    return nextStep as ReckittApplicationSteps;
  };

  const goToPreviousStep = () => {
    const steps = Object.values(ReckittApplicationSteps);
    const currentIndex = steps.findIndex(
      (step) => ReckittApplicationStep[step as ReckittApplicationSteps].url === location,
    );

    if (currentIndex === 0) {
      window.location.href = 'platform/auth/application-start?partner=reckitt';
      return;
    }

    const previousStep = steps[currentIndex - 1];
    const previousUrl = ReckittApplicationStep[previousStep as ReckittApplicationSteps].url;
    navigate(previousUrl);
    return previousStep as ReckittApplicationSteps;
  };

  const applicationData = Application.useGetProfessionalApplicationStatus();

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.waitlisted) {
    return <ApplicationWaitlisted programName='Accelerate Hub' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.rejected) {
    return <ApplicationRejected programName='Accelerate Hub' />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.approved) {
    return <ApplicationApproved cohortKickoffDate={applicationData.data?.cohortKickoffDate} clientUrlParam={applicationData.data?.clientUrlParam} />;
  }

  if (applicationData.data?.applicationStatus === ProfessionalApplicationStatusEnum.pending) {
    return <ApplicationUnderReview />;
  }

  return (
    <Switch>
      {Object.entries(ReckittApplicationStep).map(([_key, value]) => {
        const { url, component: Component, progress } = value;
        return (
          <Route key={url} path={url}>
            <Component
              onBack={goToPreviousStep}
              onContinue={goToNextStep}
              progress={progress}
              dataKey={ApplicationStorageDataKeys.PROFESSIONAL_APPLICATION}
            />
          </Route>
        );
      })}
    </Switch>
  );
};
